import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Tooltip,
  Legend,
} from "chart.js";
import { Box, Typography } from "@mui/material";
import { LoanPayment } from "../../services/loanCalculator";
import { formatCurrency } from "../../utils/namesFormater";
import { InterestType, RepaymentType } from "../../interfaces/DTO/mislaka_xml_models";
import { formatRepaymentType } from "../../utils/enumFormatter";

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, Tooltip, Legend);

interface Props {
  payments: LoanPayment[];
  startDate?: string; // תאריך התחלה בפורמט ISO (YYYY-MM-DD)
  endDate?: string; // תאריך סיום בפורמט ISO (YYYY-MM-DD)
  isExistingLoan?: boolean; // האם זו הלוואה קיימת
  interestType?: InterestType; // סוג הריבית
  repaymentType?: RepaymentType; // סוג ההחזר
}

const LoanPaymentsChart: React.FC<Props> = ({ payments, startDate, endDate, isExistingLoan, interestType, repaymentType }) => {
  // חישוב האינדקס של endDate (החודש המדווח האחרון)
  const reportedMonthIndex = useMemo(() => {
    if (!isExistingLoan || !startDate || !endDate || interestType !== InterestType.VARIABLE) {
      return payments.length; // כל התשלומים מדווחים
    }

    // Parse ISO date format: YYYY-MM-DD
    const [startYear, startMonth] = startDate.split('-').map(Number);
    const [endYear, endMonth] = endDate.split('-').map(Number);

    const monthsDiff = (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
    return monthsDiff;
  }, [isExistingLoan, startDate, endDate, interestType, payments.length]);

  // יצירת תוויות - אם יש תאריכים נשתמש בהם, אחרת נציג "חודש X"
  const labels = payments.map((p, index) => {
    if (startDate) {
      // Parse ISO date format: YYYY-MM-DD
      const [year, month] = startDate.split('-').map(Number);
      // תמיד נתחיל מיום 1 של החודש כדי למנוע בעיות עם ימים שלא קיימים בחודשים מסוימים
      const start = new Date(year, month - 1, 1); // month-1 כי JS Date מתחיל מ-0

      // הוספת חודשים
      const currentDate = new Date(start);
      currentDate.setMonth(currentDate.getMonth() + index);

      // פורמט MM-YYYY
      const mm = String(currentDate.getMonth() + 1).padStart(2, "0");
      const yyyy = currentDate.getFullYear();
      return `${mm}-${yyyy}`;
    }
    return `חודש ${p.month}`;
  });

  // בדיקה אם זו הלוואת ריבית שוטפת (כל התשלומים מלבד האחרון הם רק ריבית)
  const isCurrentInterestLoan = payments.length > 1 &&
    payments.slice(0, -1).every(p => p.principalPayment === 0);

  // נתוני הקרן והריבית - אם זו הלוואה קיימת עם ריבית משתנה, נשאיר ריק עד endDate
  const principalData = payments.map((p, index) =>
    index < reportedMonthIndex ? p.principalPayment : 0
  );
  const interestData = payments.map((p, index) =>
    index < reportedMonthIndex ? p.interestPayment : 0
  );

  // נתוני יתרת חוב - רק להלוואת ריבית שוטפת, בכל החודשים מלבד האחרון
  const debtBalanceData = isCurrentInterestLoan
    ? payments.map((p, index) => {
        // בחודשים שלפני האחרון - מציגים את יתרת החוב
        // בחודש האחרון - לא מציגים כי שם משלמים את הקרן
        return index < payments.length - 1 ? p.remainingBalance : 0;
      })
    : [];

  const datasets = [
    {
      label: "תשלום קרן",
      data: principalData,
      backgroundColor: "rgba(76, 175, 80, 0.8)", // ירוק
      stack: "stack1",
    },
    {
      label: "תשלום ריבית",
      data: interestData,
      backgroundColor: "rgba(255, 152, 0, 0.8)", // כתום
      stack: "stack1",
    },
  ];

  // הוספת יתרת חוב רק אם זו הלוואת ריבית שוטפת
  if (isCurrentInterestLoan) {
    datasets.unshift({
      label: "יתרת חוב",
      data: debtBalanceData,
      backgroundColor: "rgba(158, 158, 158, 0.5)", // אפור
      stack: "stack1",
    });
  }

  const chartData = {
    labels,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${formatCurrency(value)}`;
          },
          footer: (tooltipItems: any[]) => {
            const total = tooltipItems.reduce((sum, item) => sum + item.parsed.y, 0);
            return `סה"כ: ${formatCurrency(total)}`;
          },
        },
      },
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          font: {
            size: 14,
          },
        },
      },
      datalabels: {
        display: false, // הסרת labels מהבארים
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 11,
          },
          maxRotation: 45,
          minRotation: 45,
        },
        grid: {
          display: false,
        },
        stacked: true,
      },
      y: {
        ticks: {
          callback: (value: any) => formatCurrency(value),
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
        stacked: true,
      },
    },
  };

  // טקסט לכותרת על פי סוג ההחזר
  const repaymentTypeText = repaymentType ? ` - ${formatRepaymentType(repaymentType)}` : "";

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
        התפלגות התשלומים החודשיים{repaymentTypeText}
      </Typography>
      <Box sx={{ height: 400, width: "100%" }}>
        <Bar data={chartData} options={options} />
      </Box>
    </Box>
  );
};

export default LoanPaymentsChart;
